/**
 * The answer's Markdown, parsed into blocks the panel can render.
 *
 * Deliberately only the subset the answer prompt allows
 * (packages/core/src/use-cases/answer-question/prompts.ts, FORMAT): "### "
 * headings, **bold**, `code`, "- " list items and blank lines. Anything else is
 * shown as the text it is. There is no HTML path at all — the answer quotes
 * provider data, which is untrusted, and React escapes every string it renders.
 */
export type Inline =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'bold'; readonly text: string }
  | { readonly kind: 'code'; readonly text: string };

export type Block =
  | { readonly kind: 'heading'; readonly inline: Inline[] }
  | { readonly kind: 'list'; readonly items: Inline[][] }
  | { readonly kind: 'paragraph'; readonly inline: Inline[] };

export function parseAnswer(markdown: string): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  let list: Inline[][] = [];

  const flush = (): void => {
    if (paragraph.length > 0)
      blocks.push({ kind: 'paragraph', inline: parseInline(paragraph.join('\n')) });
    if (list.length > 0) blocks.push({ kind: 'list', items: list });
    paragraph = [];
    list = [];
  };

  for (const raw of markdown.replace(/\r\n/g, '\n').split('\n')) {
    const line = raw.trimEnd();
    const heading = /^#{1,6}\s+(.*)$/.exec(line);
    const item = /^\s*[-*•]\s+(.*)$/.exec(line);

    if (line.trim() === '') {
      flush();
    } else if (heading?.[1] !== undefined) {
      flush();
      blocks.push({ kind: 'heading', inline: parseInline(heading[1]) });
    } else if (item?.[1] !== undefined) {
      if (paragraph.length > 0) flush();
      list.push(parseInline(item[1]));
    } else {
      if (list.length > 0) flush();
      paragraph.push(line);
    }
  }
  flush();
  return blocks;
}

/** **bold** and `code`; an unmatched marker is left as the text it is. */
export function parseInline(text: string): Inline[] {
  const parts: Inline[] = [];
  const pattern = /\*\*(.+?)\*\*|`([^`]+)`/g;
  let last = 0;
  for (const match of text.matchAll(pattern)) {
    const at = match.index;
    if (at > last) parts.push({ kind: 'text', text: text.slice(last, at) });
    if (match[1] !== undefined) parts.push({ kind: 'bold', text: match[1] });
    else if (match[2] !== undefined) parts.push({ kind: 'code', text: match[2] });
    last = at + match[0].length;
  }
  if (last < text.length) parts.push({ kind: 'text', text: text.slice(last) });
  return parts;
}
